import { Alert, Box, Button, Grid, Paper, Stack, Typography } from "@mui/material"
import { memo, useCallback, useEffect, useReducer, useState } from "react"
import AddIcon  from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'

import UserParametersContext     from "../../../../components/UserParameters/context"
import useInternalFetch          from "../../../../api/apiInternal"
import Loading                   from "../../../../components/Loading"
import GeneralError              from "../../../../components/GeneralError"
import { convertDbNumberToHtml } from "../../../../common/Helper"
import config                    from "../../../../common/Config"
import RiskClauseCard            from "./RiskClauseCard"

const RiskPanel = ({ fields, contextId, onSaved }) => {
	const internalFetch                         = useInternalFetch()
	const [riskClauses, dispatch]               = useReducer(reducer, [{}])
	const [highlightErrors, setHighlightErrors] = useState(false)
	const [isLoading, setIsLoading]             = useState(true)
	const [isSaving, setIsSaving]               = useState(false)
	const [fatalError, setFatalError]           = useState(false)
	const [message, setMessage]                 = useState(null)

	useEffect(() => {
		setIsLoading(true)
		const url = config.apiUrl + '/api/settings/riskParameters' + (contextId ? `?contextId=${contextId}` : '')
		internalFetch(url).then(results => {
			if (results?.status) { setFatalError(true); setIsLoading(false); return }
			dispatch({ type: 'load', payload: results })
			setIsLoading(false)
		})
	}, [contextId])

	const handleAdd    = () => dispatch({ type: 'add' })
	const handleRemove = useCallback((index) => dispatch({ type: 'remove', index }), [])
	const handleChange = useCallback((clause, index) => dispatch({ type: 'update', index, payload: clause }), [])

	const handleSave = () => {
		setIsSaving(true)
		const isAllValid = riskClauses.reduce((acc, rc) => acc && validate(rc), true)
		if (isAllValid) {
			internalFetch(config.apiUrl + '/api/settings/riskParameters/', {
				method  : 'POST',
				headers : { 'content-type': 'application/json; charset=utf-8' },
				body    : JSON.stringify({ contextId: contextId ?? null, params: riskClauses }),
			}).then(results => {
				setMessage(results?.status
					? { severity: 'error',   text: 'Erro ao salvar parâmetros.' }
					: { severity: 'success', text: 'Parâmetros salvos com sucesso.' }
				)
				if (!results?.status) onSaved?.()
				setIsSaving(false)
				setHighlightErrors(false)
			})
		} else {
			setIsSaving(false)
			setHighlightErrors(true)
			setMessage({ severity: 'error', text: 'Preencha corretamente todos os campos.' })
		}
	}

	if (fatalError) return <GeneralError message="Erro ao carregar parâmetros de risco" />

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
				Parâmetros de Risco
			</Typography>

			{isLoading ? <Loading paddingBottom={2} /> : (
				<UserParametersContext.Provider value={{ fields }}>
					{message && (
						<Alert severity={message.severity} sx={{ mb: 1.5, py: 0 }} onClose={() => setMessage(null)}>
							{message.text}
						</Alert>
					)}

					<Box sx={{ pb: 2 }}>
						<Grid container spacing={2} alignItems="flex-start">
							{riskClauses.map((clause, index) => (
								<Grid item xs={12} md={6} lg={12} key={index}>
									<RiskClauseCard
										index={index}
										value={clause}
										highlightErrors={highlightErrors}
										onChange={handleChange}
										onDelete={handleRemove}
									/>
								</Grid>
							))}
						</Grid>
					</Box>

					<Stack direction="row" justifyContent="flex-end" spacing={1}>
						<Button size="small" startIcon={<AddIcon />} onClick={handleAdd}>
							Adicionar campo
						</Button>
						<Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={isSaving}>
							Salvar
						</Button>
					</Stack>
				</UserParametersContext.Provider>
			)}
		</Paper>
	)
}

const reducer = (state, action) => {
	switch (action.type) {
		case 'add':
			return [...state, {}]
		case 'remove': {
			const next = [...state]
			next.splice(action.index, 1)
			return next
		}
		case 'load':
			return action.payload.length > 0
				? action.payload.map(r => ({
					field            : r.field,
					lowRiskParameter : { operator: r.lowRiskOperator, parameter: convertDbNumberToHtml(r.lowRiskParameter) },
					medRiskParameter : { operator: r.medRiskOperator, parameter: convertDbNumberToHtml(r.medRiskParameter) },
					higRiskParameter : { operator: r.higRiskOperator, parameter: convertDbNumberToHtml(r.higRiskParameter) },
				}))
				: [{}]
		case 'update': {
			const next = [...state]
			next[action.index] = { ...action.payload, isValid: validate(action.payload) }
			return next
		}
		default:
			return state
	}
}

const validate = (clause) =>
	!!(clause.field &&
		clause.lowRiskParameter?.operator && clause.lowRiskParameter?.parameter !== '' &&
		clause.medRiskParameter?.operator && clause.medRiskParameter?.parameter !== '' &&
		clause.higRiskParameter?.operator && clause.higRiskParameter?.parameter !== '')

export default memo(RiskPanel)
